import { api } from '../client';
import type {
  TrendingTokensResponse,
  GoodTradersResponse,
  GoodTradersForTokenResponse,
  PositionsResponse,
  TokenPositionResponse,
  TokenPositionSummaryResponse,
  WalletHoldingsResponse,
  WalletHoldingsParams,
  NativeBalancesResponse,
  RefreshTokenHoldingResponse,
  HoldingsRefresh,
} from '@/types/api/trading';

/**
 * Trading API endpoints
 */
export const tradingAPI = {
  /**
   * Get trending tokens
   * @param limit - Optional limit for number of tokens (default from backend)
   */
  getTrendingTokens: async (limit?: number): Promise<TrendingTokensResponse> => {
    const params = limit ? { limit } : undefined;
    return api.get<TrendingTokensResponse>('/trading/dashboard.json', { params });
  },

  /**
   * Get good trader candidates
   * @param hours - Decision window in hours (default: 24)
   * @param minGoodTraders - Minimum number of good traders (default: 1)
   */
  getGoodTraders: async (hours?: number, minGoodTraders?: number): Promise<GoodTradersResponse> => {
    const params: Record<string, number> = {};
    if (hours !== undefined) params.hours = hours;
    if (minGoodTraders !== undefined) params.good = minGoodTraders;
    return api.get<GoodTradersResponse>('/trading/dashboard/good_traders.json', { params });
  },

  /**
   * Get good traders for a specific token
   * @param tokenAddress - Token contract address
   * @param hours - Decision window in hours (default: 24)
   */
  getGoodTradersForToken: async (
    tokenAddress: string,
    hours?: number
  ): Promise<GoodTradersForTokenResponse> => {
    const params = hours !== undefined ? { hours } : undefined;
    return api.get<GoodTradersForTokenResponse>(
      `/trading/dashboard/good_traders/token/${tokenAddress}.json`,
      { params }
    );
  },

  /**
   * Get portfolio positions across all chains
   */
  getPositions: async (): Promise<PositionsResponse> => {
    return api.get<PositionsResponse>('/trading/positions.json');
  },

  /**
   * Get transaction history for a specific token position
   * @param tokenAddress - Token contract address
   */
  getPositionForToken: async (tokenAddress: string): Promise<TokenPositionResponse> => {
    return api.get<TokenPositionResponse>(`/trading/positions/token/${tokenAddress}.json`);
  },

  /**
   * Get current user's token position summary for one chain/token
   */
  getTokenPositionSummary: async (
    tokenAddress: string,
    chainId: number
  ): Promise<TokenPositionSummaryResponse> => {
    return api.get<TokenPositionSummaryResponse>(`/trading/positions/token/${tokenAddress}.json`, {
      params: { chain_id: chainId },
    });
  },

  /**
   * Get on-chain wallet holdings across all chains
   * Supports server-side filtering, sorting, and pagination.
   */
  getHoldings: async (options: WalletHoldingsParams = {}): Promise<WalletHoldingsResponse> => {
    const params = {
      ...(options.chainId ? { chain_id: options.chainId } : {}),
      ...(options.query?.trim() ? { q: options.query.trim() } : {}),
      ...(options.sortBy ? { sort_by: options.sortBy } : {}),
      ...(options.sortDirection ? { sort_direction: options.sortDirection } : {}),
      ...(options.page ? { page: options.page } : {}),
      ...(options.perPage ? { per_page: options.perPage } : {}),
    };
    return api.get<WalletHoldingsResponse>('/trading/holdings.json', { params });
  },

  /**
   * Trigger manual refresh of the wallet's holdings for a chain
   */
  refreshHoldings: async (chainId?: number): Promise<HoldingsRefresh> => {
    const params = chainId ? { chain_id: chainId } : undefined;
    return api.post<HoldingsRefresh>('/api/trading/wallet/holdings/refresh', undefined, { params });
  },

  /**
   * Get one holdings refresh status for socket fallback/reload recovery
   */
  getHoldingsRefresh: async (refreshId: number): Promise<HoldingsRefresh> => {
    return api.get<HoldingsRefresh>(`/api/trading/wallet/holdings/refreshes/${refreshId}`);
  },

  /**
   * Refresh the wallet's on-chain balance for one token
   */
  refreshTokenHolding: async (
    tokenAddress: string,
    chainId: number
  ): Promise<RefreshTokenHoldingResponse> => {
    return api.post<RefreshTokenHoldingResponse>('/api/trading/wallet/holdings/refresh', undefined, {
      params: { chain_id: chainId, token_address: tokenAddress },
    });
  },

  /**
   * Get native token balances (BNB, ETH, SOL) across all chains
   */
  getNativeBalances: async (): Promise<NativeBalancesResponse> => {
    return api.get<NativeBalancesResponse>('/trading/native_balances.json');
  },
};
